import { useState, useCallback } from 'react';
import { ChevronRight, ChevronDown, Search, RefreshCw, GitBranch, Hash } from 'lucide-react';
import { cn } from '@/utils/cn';
import { Spinner } from '@/components/ui/Spinner';
import type { CostCenterTreeNodeDto } from '@/types/costCenter.types';

// ── Tree Node ─────────────────────────────────────────────────────────────────

interface TreeNodeProps {
  node:        CostCenterTreeNodeDto;
  depth:       number;
  selected:    string | null;
  expanded:    Set<string>;
  searchQuery: string;
  onSelect:    (node: CostCenterTreeNodeDto) => void;
  onToggle:    (id: string) => void;
}

function matchesSearch(node: CostCenterTreeNodeDto, q: string): boolean {
  if (!q) return true;
  const lower = q.toLowerCase();
  return (
    node.code.toLowerCase().includes(lower) ||
    node.name.toLowerCase().includes(lower) ||
    (node.shortName?.toLowerCase().includes(lower) ?? false)
  );
}

function subtreeHasMatch(node: CostCenterTreeNodeDto, q: string): boolean {
  if (!q) return true;
  if (matchesSearch(node, q)) return true;
  return node.children.some(c => subtreeHasMatch(c, q));
}

const TreeNode = ({
  node, depth, selected, expanded, searchQuery, onSelect, onToggle,
}: TreeNodeProps) => {
  const isExpanded  = expanded.has(node.id);
  const isSelected  = selected === node.id;
  const hasChildren = node.children.length > 0;

  const visibleChildren = searchQuery
    ? node.children.filter(c => subtreeHasMatch(c, searchQuery))
    : node.children;

  const isVisible = searchQuery ? subtreeHasMatch(node, searchQuery) : true;
  if (!isVisible) return null;

  const showChildren = hasChildren && (searchQuery ? true : isExpanded);

  return (
    <div>
      <button
        type="button"
        onClick={() => onSelect(node)}
        onDoubleClick={() => hasChildren && onToggle(node.id)}
        className={cn(
          'w-full flex items-center gap-1 text-left group transition-colors',
          'hover:bg-gray-100 dark:hover:bg-gray-700/60',
          isSelected && 'bg-indigo-50 dark:bg-indigo-900/30 hover:bg-indigo-50 dark:hover:bg-indigo-900/30',
          !node.isActive && 'opacity-50',
        )}
        style={{ paddingLeft: `${depth * 16 + 4}px`, paddingTop: '3px', paddingBottom: '3px', paddingRight: '8px' }}
        title={`${node.code} — ${node.name}`}
      >
        {/* Expand/collapse */}
        <span
          className={cn(
            'flex-shrink-0 w-4 h-4 flex items-center justify-center rounded',
            'text-gray-400 group-hover:text-gray-600',
            hasChildren && 'hover:bg-gray-200 dark:hover:bg-gray-600 cursor-pointer',
          )}
          onClick={e => { e.stopPropagation(); if (hasChildren) onToggle(node.id); }}
        >
          {hasChildren
            ? (isExpanded || searchQuery
                ? <ChevronDown size={12} />
                : <ChevronRight size={12} />)
            : <span className="w-3 h-3" />}
        </span>

        {/* Icon */}
        <span className="flex-shrink-0">
          {node.allowPosting
            ? <Hash size={12} className="text-indigo-500 dark:text-indigo-400 opacity-60" />
            : <GitBranch size={12} className="text-indigo-600 dark:text-indigo-300 opacity-70" />}
        </span>

        {/* Code */}
        <span className={cn(
          'flex-shrink-0 font-mono text-[10px] font-semibold w-16 truncate',
          isSelected ? 'text-indigo-700 dark:text-indigo-300' : 'text-gray-500 dark:text-gray-400',
        )}>
          {node.code}
        </span>

        {/* Name */}
        <span className={cn(
          'flex-1 text-xs truncate',
          isSelected
            ? 'text-indigo-800 dark:text-indigo-200 font-medium'
            : 'text-gray-700 dark:text-gray-300',
          !node.allowPosting && 'font-medium',
        )}>
          {node.name}
        </span>

        {/* Badges */}
        {node.budgetControl && (
          <span className="flex-shrink-0 text-[9px] px-1 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 font-semibold">
            BUDGET
          </span>
        )}
        {!node.isActive && (
          <span className="flex-shrink-0 text-[9px] px-1 rounded bg-gray-100 dark:bg-gray-700 text-gray-400 font-semibold">
            OFF
          </span>
        )}
      </button>

      {/* Children */}
      {showChildren && visibleChildren.length > 0 && (
        <div>
          {visibleChildren.map(child => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selected={selected}
              expanded={expanded}
              searchQuery={searchQuery}
              onSelect={onSelect}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

export interface CostCenterTreePanelProps {
  nodes:      CostCenterTreeNodeDto[];
  loading:    boolean;
  selectedId: string | null;
  onSelect:   (node: CostCenterTreeNodeDto) => void;
  onRefresh:  () => void;
}

export const CostCenterTreePanel = ({
  nodes, loading, selectedId, onSelect, onRefresh,
}: CostCenterTreePanelProps) => {
  const [expanded,    setExpanded]    = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');

  const toggleNode = useCallback((id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const expandAll = () => {
    const allIds = new Set<string>();
    const collect = (n: CostCenterTreeNodeDto) => { allIds.add(n.id); n.children.forEach(collect); };
    nodes.forEach(collect);
    setExpanded(allIds);
  };

  const collapseAll = () => setExpanded(new Set());

  const countTree = (n: CostCenterTreeNodeDto): number => 1 + n.children.reduce((s, c) => s + countTree(c), 0);
  const totalCount = nodes.reduce((sum, n) => sum + countTree(n), 0);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700">

      {/* Panel header */}
      <div className="flex-shrink-0 px-3 py-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
            Cost Centres
          </span>
          <div className="flex items-center gap-1">
            <button type="button" onClick={expandAll}
              className="text-[10px] text-gray-400 hover:text-indigo-600 px-1">
              Expand
            </button>
            <span className="text-gray-300 dark:text-gray-600">|</span>
            <button type="button" onClick={collapseAll}
              className="text-[10px] text-gray-400 hover:text-indigo-600 px-1">
              Collapse
            </button>
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              title="Refresh tree"
              className="ml-1 w-5 h-5 flex items-center justify-center rounded text-gray-400 hover:text-indigo-600 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              {loading ? <Spinner size="sm" /> : <RefreshCw size={11} />}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search cost centres…"
            className={cn(
              'w-full h-7 pl-6 pr-2 rounded border text-xs',
              'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100',
              'border-gray-300 dark:border-gray-600',
              'placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500',
            )}
          />
        </div>

        <div className="mt-1 text-[10px] text-gray-400">
          {totalCount} cost centre{totalCount !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Tree body */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden select-none">
        {loading && (
          <div className="flex items-center justify-center h-20">
            <Spinner size="sm" />
          </div>
        )}

        {!loading && nodes.length === 0 && (
          <div className="px-4 py-8 text-center text-xs text-gray-400">
            No cost centres found.<br />
            <span className="text-gray-300">Use the form to create the first cost centre.</span>
          </div>
        )}

        {!loading && (
          <div className="py-0.5">
            {nodes.map(node => (
              <TreeNode
                key={node.id}
                node={node}
                depth={0}
                selected={selectedId}
                expanded={expanded}
                searchQuery={searchQuery}
                onSelect={onSelect}
                onToggle={toggleNode}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
